import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Receipt, Search, DollarSign, Clock, CheckCircle, AlertTriangle, TrendingUp, Banknote, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { format } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const statusLabels: Record<string, string> = { pending: "معلقة", paid: "مدفوعة", overdue: "متأخرة", cancelled: "ملغاة" };
const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  paid: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  overdue: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  cancelled: "bg-muted text-muted-foreground",
};
const roleLabels: Record<string, string> = { teacher: "مدرس/ة", supervisor: "مشرف/ة", admin: "إداري/ة", driver: "سائق", cleaner: "عامل نظافة" };
const CHART_COLORS = ["hsl(var(--primary))", "#22c55e", "#eab308", "#ef4444", "#8b5cf6"];
const txTypeLabels: Record<string, string> = { invoice_payment: "دفع فاتورة", salary: "راتب", expense: "مصروف", refund: "استرداد", subscription: "اشتراك" };

const AdminBilling = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterNursery, setFilterNursery] = useState("all");

  const { data: invoices, isLoading } = useQuery({
    queryKey: ["admin-all-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase.from("invoices").select("*, children:child_id(full_name), nurseries:nursery_id(name)").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: expenses } = useQuery({
    queryKey: ["admin-all-expenses"],
    queryFn: async () => {
      const { data, error } = await supabase.from("expenses").select("*, nurseries:nursery_id(name)").order("expense_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: payrollRecords } = useQuery({
    queryKey: ["admin-all-payroll"],
    queryFn: async () => {
      const { data, error } = await supabase.from("payroll_records").select("*, nurseries:nursery_id(name)").order("year", { ascending: false }).order("month", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: transactions } = useQuery({
    queryKey: ["admin-all-transactions"],
    queryFn: async () => {
      const { data, error } = await supabase.from("financial_transactions").select("*, nurseries:nursery_id(name)").order("transaction_date", { ascending: false }).limit(500);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: nurseries } = useQuery({
    queryKey: ["admin-nurseries-billing"],
    queryFn: async () => {
      const { data } = await supabase.from("nurseries").select("id, name");
      return data || [];
    },
  });

  const stats = useMemo(() => {
    if (!invoices) return { total: 0, totalAmount: 0, paid: 0, paidAmount: 0, pending: 0, pendingAmount: 0, overdue: 0, overdueAmount: 0 };
    const now = new Date();
    return {
      total: invoices.length,
      totalAmount: invoices.reduce((s, i) => s + (i.amount || 0), 0),
      paid: invoices.filter(i => i.status === "paid").length,
      paidAmount: invoices.filter(i => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0),
      pending: invoices.filter(i => i.status === "pending").length,
      pendingAmount: invoices.filter(i => i.status === "pending").reduce((s, i) => s + (i.amount || 0), 0),
      overdue: invoices.filter(i => i.status === "pending" && new Date(i.due_date) < now).length,
      overdueAmount: invoices.filter(i => i.status === "pending" && new Date(i.due_date) < now).reduce((s, i) => s + (i.amount || 0), 0),
    };
  }, [invoices]);

  const totalExpenses = useMemo(() => (expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0), [expenses]);
  const totalPayroll = useMemo(() => (payrollRecords || []).reduce((s, p) => s + Number(p.net_salary || 0), 0), [payrollRecords]);

  const revenueByNursery = useMemo(() => {
    if (!invoices) return [];
    const map: Record<string, { name: string; paid: number; pending: number }> = {};
    invoices.forEach(inv => {
      const name = (inv as any).nurseries?.name || "غير محدد";
      if (!map[name]) map[name] = { name, paid: 0, pending: 0 };
      if (inv.status === "paid") map[name].paid += inv.amount || 0;
      else if (inv.status === "pending") map[name].pending += inv.amount || 0;
    });
    return Object.values(map).sort((a, b) => (b.paid + b.pending) - (a.paid + a.pending)).slice(0, 8);
  }, [invoices]);

  const statusDist = useMemo(() => {
    if (!invoices) return [];
    const counts: Record<string, number> = {};
    invoices.forEach(i => { counts[i.status] = (counts[i.status] || 0) + 1; });
    return Object.entries(counts).map(([status, count]) => ({ name: statusLabels[status] || status, value: count }));
  }, [invoices]);

  const filtered = useMemo(() => {
    if (!invoices) return [];
    const now = new Date();
    return invoices.filter(inv => {
      const childName = (inv as any).children?.full_name || "";
      const nurseryName = (inv as any).nurseries?.name || "";
      if (searchTerm && !childName.includes(searchTerm) && !nurseryName.includes(searchTerm) && !inv.title.includes(searchTerm)) return false;
      if (filterStatus === "overdue") return inv.status === "pending" && new Date(inv.due_date) < now;
      if (filterStatus !== "all" && inv.status !== filterStatus) return false;
      if (filterNursery !== "all" && inv.nursery_id !== filterNursery) return false;
      return true;
    });
  }, [invoices, searchTerm, filterStatus, filterNursery]);

  const filteredExpenses = useMemo(() => {
    if (!expenses) return [];
    return expenses.filter(e => {
      if (filterNursery !== "all" && e.nursery_id !== filterNursery) return false;
      if (searchTerm && !e.title.includes(searchTerm)) return false;
      return true;
    });
  }, [expenses, filterNursery, searchTerm]);

  const filteredPayroll = useMemo(() => {
    if (!payrollRecords) return [];
    return payrollRecords.filter(p => {
      if (filterNursery !== "all" && p.nursery_id !== filterNursery) return false;
      if (searchTerm && !p.staff_name.includes(searchTerm)) return false;
      return true;
    });
  }, [payrollRecords, filterNursery, searchTerm]);

  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];
    return transactions.filter(t => {
      if (filterNursery !== "all" && t.nursery_id !== filterNursery) return false;
      if (searchTerm && !(t.description || "").includes(searchTerm) && !(t.staff_name || "").includes(searchTerm)) return false;
      return true;
    });
  }, [transactions, filterNursery, searchTerm]);

  const allMonths = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold">الحسابات والفواتير</h1>
        <p className="text-muted-foreground text-sm">نظرة شاملة على جميع المعاملات المالية في الحضانات</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Receipt className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي الفواتير</p>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">{stats.totalAmount.toLocaleString()} ج.م</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><ArrowUpRight className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">المدفوعة</p>
              <p className="text-2xl font-bold">{stats.paid}</p>
              <p className="text-xs text-green-600">{stats.paidAmount.toLocaleString()} ج.م</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10"><ArrowDownRight className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي المصروفات</p>
              <p className="text-2xl font-bold text-destructive">{totalExpenses.toLocaleString()} ج.م</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/10"><Banknote className="w-5 h-5 text-yellow-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي الرواتب المؤرشفة</p>
              <p className="text-2xl font-bold">{totalPayroll.toLocaleString()} ج.م</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Common Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterNursery} onValueChange={setFilterNursery}>
              <SelectTrigger className="w-44"><SelectValue placeholder="الحضانة" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحضانات</SelectItem>
                {nurseries?.map(n => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="invoices" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="invoices">الفواتير</TabsTrigger>
          <TabsTrigger value="expenses">المصروفات</TabsTrigger>
          <TabsTrigger value="payroll">الرواتب</TabsTrigger>
          <TabsTrigger value="ledger">السجل المالي</TabsTrigger>
          <TabsTrigger value="charts">الرسوم البيانية</TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="flex gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="الحالة" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="pending">معلقة</SelectItem>
                <SelectItem value="paid">مدفوعة</SelectItem>
                <SelectItem value="overdue">متأخرة</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5" /> الفواتير ({filtered.length})</CardTitle></CardHeader>
            <CardContent>
              {isLoading ? <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>العنوان</TableHead>
                      <TableHead>الحضانة</TableHead>
                      <TableHead>الطفل</TableHead>
                      <TableHead>المبلغ</TableHead>
                      <TableHead>الحالة</TableHead>
                      <TableHead>تاريخ الاستحقاق</TableHead>
                      <TableHead>تاريخ الدفع</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(inv => {
                      const isOverdue = inv.status === "pending" && new Date(inv.due_date) < new Date();
                      return (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.title}</TableCell>
                          <TableCell><Badge variant="outline">{(inv as any).nurseries?.name || "—"}</Badge></TableCell>
                          <TableCell>{(inv as any).children?.full_name || "—"}</TableCell>
                          <TableCell className="font-bold">{inv.amount.toLocaleString()} ج.م</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${isOverdue ? statusColors.overdue : (statusColors[inv.status] || "")}`}>
                              {isOverdue ? "متأخرة" : (statusLabels[inv.status] || inv.status)}
                            </span>
                          </TableCell>
                          <TableCell>{format(new Date(inv.due_date), "dd/MM/yyyy")}</TableCell>
                          <TableCell>{inv.paid_at ? format(new Date(inv.paid_at), "dd/MM/yyyy") : "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!filtered.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">لا توجد فواتير</TableCell></TableRow>}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><ArrowDownRight className="w-5 h-5" /> المصروفات ({filteredExpenses.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>العنوان</TableHead>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>الفئة</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>التاريخ</TableHead>
                    <TableHead>متكرر</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.title}</TableCell>
                      <TableCell><Badge variant="outline">{(e as any).nurseries?.name || "—"}</Badge></TableCell>
                      <TableCell className="text-sm">{e.category}</TableCell>
                      <TableCell className="font-bold text-destructive">{Number(e.amount).toLocaleString()} ج.م</TableCell>
                      <TableCell>{format(new Date(e.expense_date), "dd/MM/yyyy")}</TableCell>
                      <TableCell>{e.is_recurring ? <Badge className="bg-primary/10 text-primary">متكرر</Badge> : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {!filteredExpenses.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد مصروفات</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Banknote className="w-5 h-5" /> سجل الرواتب ({filteredPayroll.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الموظف</TableHead>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>الوظيفة</TableHead>
                    <TableHead>الشهر</TableHead>
                    <TableHead>صافي الراتب</TableHead>
                    <TableHead>الحالة</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayroll.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.staff_name}</TableCell>
                      <TableCell><Badge variant="outline">{(p as any).nurseries?.name || "—"}</Badge></TableCell>
                      <TableCell>{roleLabels[p.staff_role] || p.staff_role}</TableCell>
                      <TableCell>{allMonths[p.month - 1]} {p.year}</TableCell>
                      <TableCell className="font-bold">{Number(p.net_salary).toLocaleString()} ج.م</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.status === "paid" ? statusColors.paid : statusColors.pending}`}>
                          {p.status === "paid" ? "مدفوع" : "معلق"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filteredPayroll.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد سجلات رواتب</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ledger Tab */}
        <TabsContent value="ledger">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" /> السجل المالي ({filteredTransactions.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>التاريخ</TableHead>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>النوع</TableHead>
                    <TableHead>الاتجاه</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>الوصف</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className="text-sm">{format(new Date(t.transaction_date), "dd/MM/yyyy")}</TableCell>
                      <TableCell><Badge variant="outline">{(t as any).nurseries?.name || "—"}</Badge></TableCell>
                      <TableCell><Badge variant="outline">{txTypeLabels[t.transaction_type] || t.transaction_type}</Badge></TableCell>
                      <TableCell>
                        {t.direction === "in" ? (
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">وارد</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">صادر</Badge>
                        )}
                      </TableCell>
                      <TableCell className={`font-bold ${t.direction === "in" ? "text-green-600" : "text-destructive"}`}>
                        {t.direction === "in" ? "+" : "-"}{Number(t.amount).toLocaleString()} ج.م
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{t.description || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {!filteredTransactions.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد معاملات</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Charts Tab */}
        <TabsContent value="charts" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="w-5 h-5 text-primary" /> الإيرادات حسب الحضانة</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueByNursery} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(val: number) => `${val.toLocaleString()} ج.م`} />
                      <Bar dataKey="paid" name="مدفوع" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="pending" name="معلق" fill="#eab308" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Receipt className="w-5 h-5 text-primary" /> توزيع حالات الفواتير</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusDist} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                        {statusDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminBilling;
